<template>
  <TransitionRoot as="template" :show="open">
    <Dialog as="div" class="fixed inset-0 z-[3600]" @close="close">
      <div class="fixed inset-0 bg-zinc-950/70 backdrop-blur-sm" />
      <div class="fixed inset-0 overflow-y-auto p-4 sm:p-6">
        <div class="flex min-h-full items-center justify-center">
          <DialogPanel class="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl sm:p-8">
            <div class="flex items-start justify-between gap-4">
              <div>
                <DialogTitle class="text-2xl font-bold text-zinc-900">Contact Sales</DialogTitle>
                <p class="mt-2 text-sm leading-6 text-zinc-600">Tell us about your business and our team will help you with {{ plan?.name || 'an Enterprise plan' }}.</p>
              </div>
              <button type="button" class="rounded-lg p-2 text-zinc-500 hover:bg-zinc-100" aria-label="Close Contact Sales" :disabled="submitting" @click="close"><XMarkIcon class="h-5 w-5" /></button>
            </div>
            <div v-if="submitted" class="py-10 text-center">
              <div class="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-3xl text-emerald-600">✓</div>
              <h3 class="mt-5 text-xl font-semibold text-zinc-900">Thank you for reaching out!</h3>
              <p class="mt-2 text-sm text-zinc-600">Our team will be in touch soon.</p>
              <button type="button" class="mt-6 rounded-lg bg-[#2D8CFF] px-5 py-2.5 text-sm font-semibold text-white" @click="close">Close</button>
            </div>
            <form v-else class="mt-6 space-y-4" @submit.prevent="submit">
              <div class="grid gap-4 sm:grid-cols-2">
                <label class="block text-sm font-semibold text-zinc-700">First name *<input v-model.trim="form.firstName" required maxlength="120" autocomplete="given-name" class="contact-input mt-1" /></label>
                <label class="block text-sm font-semibold text-zinc-700">Last name *<input v-model.trim="form.lastName" required maxlength="120" autocomplete="family-name" class="contact-input mt-1" /></label>
              </div>
              <label class="block text-sm font-semibold text-zinc-700">Work email *<input v-model.trim="form.email" required type="email" maxlength="254" autocomplete="email" class="contact-input mt-1" /></label>
              <div class="grid gap-4 sm:grid-cols-2">
                <label class="block text-sm font-semibold text-zinc-700">Phone<input v-model.trim="form.phone" type="tel" maxlength="80" autocomplete="tel" class="contact-input mt-1" /></label>
                <label class="block text-sm font-semibold text-zinc-700">Company<input v-model.trim="form.company" maxlength="190" autocomplete="organization" class="contact-input mt-1" /></label>
              </div>
              <label class="block text-sm font-semibold text-zinc-700">Size of current audience *
                <select v-model="form.audienceSize" required class="contact-input mt-1">
                  <option value="0-500">0–500</option><option value="500-10k">500–10k</option><option value="10k-100k">10k–100k</option><option value="100k - 1M">100k–1M</option><option value="1 Million +">1 million+</option>
                </select>
              </label>
              <p v-if="errorMessage" class="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">{{ errorMessage }}</p>
              <div class="flex flex-col gap-4 border-t border-zinc-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                <p class="max-w-sm text-xs leading-5 text-zinc-500">By submitting, you confirm you have read the Platforma <a href="https://platformarea.com/privacy" target="_blank" rel="noopener noreferrer" class="font-semibold text-[#2D8CFF] hover:underline">Privacy Policy</a>.</p>
                <button type="submit" class="rounded-lg bg-[#2D8CFF] px-6 py-3 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60" :disabled="submitting">{{ submitting ? 'Sending…' : 'Submit' }}</button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { reactive, ref, watch } from 'vue';
import { Dialog, DialogPanel, DialogTitle, TransitionRoot } from '@headlessui/vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';

const props = defineProps({ open: Boolean, plan: { type: Object, default: null } });
const emit = defineEmits(['close']);
const form = reactive({ firstName: '', lastName: '', email: '', phone: '', company: '', audienceSize: '0-500' });
const submitting = ref(false);
const submitted = ref(false);
const errorMessage = ref('');
const apiBase = String(process.env.VUE_APP_API_PROXY_URL || '').replace(/\/+$/, '');

function close() { if (!submitting.value) emit('close'); }
async function prefillCreator() {
  try {
    const response = await fetch(`${apiBase}/api/creator-details`, { credentials: 'include', cache: 'no-store' });
    if (!response.ok) return;
    const data = await response.json();
    if (!form.firstName) form.firstName = String(data.creatorFirstName || data.firstName || '');
    if (!form.lastName) form.lastName = String(data.creatorLastName || data.lastName || '');
    if (!form.email) form.email = String(data.creatorEmail || data.email || '');
  } catch (_error) { /* The form remains editable if profile lookup is unavailable. */ }
}
async function submit() {
  if (submitting.value) return;
  submitting.value = true;
  errorMessage.value = '';
  try {
    const response = await fetch(`${apiBase}/api/platforma-site-leads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify({ ...form, source: 'contact_sales', sourcePage: window.location.pathname, sourceTitle: `Creator plan inquiry: ${props.plan?.name || 'Enterprise'}`, siteHost: window.location.host }),
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || result.success === false) throw new Error(result.message || 'Unable to submit your inquiry. Please try again.');
    submitted.value = true;
  } catch (error) { errorMessage.value = error?.message || 'Unable to submit your inquiry. Please try again.'; }
  finally { submitting.value = false; }
}
watch(() => props.open, (open) => { if (open) { submitted.value = false; errorMessage.value = ''; prefillCreator(); } });
</script>

<style scoped>
.contact-input { display: block; width: 100%; border: 1px solid #d4d4d8; border-radius: 0.625rem; padding: 0.65rem 0.75rem; color: #18181b; font-size: 0.875rem; font-weight: 400; background: white; }
.contact-input:focus { border-color: #2D8CFF; outline: 2px solid rgb(45 140 255 / 20%); }
</style>
